import React, { useEffect, useState } from 'react';
import {
  Card, Space, Typography, Radio, Skeleton, Empty, message,
  Descriptions, Tooltip, Popconfirm, Button, Result, Badge, Alert
} from 'antd';
import {
  HomeOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  ApartmentOutlined,
  IdcardOutlined,
  BankOutlined,
  MailOutlined,
  ProfileOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import InvoiceForm from './InvoiceForm';
import { useLanguage } from '../../providers/LanguageProvider';
import InvoiceProfileCard from 'src/components/invoiceProfile/InvoiceProfileCard';
import InvoiceProfileLoadingItems from 'src/components/invoiceProfile/InvoiceProfileLoadingItems';

const { Title, Text } = Typography;

export default function InvoiceProfiles() {
  const { t } = useLanguage();
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  // modal state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const load = async (showMessage = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/invoice-info');
      const data: any = Array.isArray(res.data) ? res.data : res;
      setItems(data);
      const first = data.find((x: any) => x.IsActive) || data[0];
      setSelectedId(first?.Id ?? null);

      if (showMessage && data.length > 0) {
        message.success(t('INVOICE_PROFILES_LOADED'));
      }
    } catch (e: any) {
      console.error('Invoice profiles load error:', e);
      setError(e?.response?.data?.message || e?.message || t('INVOICE_PROFILES_LOAD_ERROR'));
      message.error(t('INVOICE_PROFILES_LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async (id: number) => {
    setActionLoading((prev) => ({ ...prev, select: true }));
    try {
      await api.post(`/invoice-info/select/${id}`);
      setSelectedId(id);
      message.success(t('INVOICE_DEFAULT_UPDATED'));
    } catch (e: any) {
      console.error('Invoice profile select error:', e);
      message.error(e?.response?.data?.message || t('INVOICE_SELECTION_FAILED'));
    } finally {
      setActionLoading((prev) => ({ ...prev, select: false }));
    }
  };

  const handleDelete = async (id: number) => {
    setActionLoading((prev) => ({ ...prev, [`delete-${id}`]: true }));
    try {
      await api.delete(`/invoice-info/${id}`);
      setItems((prev) => prev.filter((x: any) => x.Id !== id));

      // Eğer silinen profil varsayılan ise, ilk profili varsayılan yap
      if (selectedId === id) {
        const remainingItems = items.filter((x: any) => x.Id !== id);
        if (remainingItems.length > 0) {
          setSelectedId(remainingItems[0].Id);
          message.info(t('INVOICE_DEFAULT_DELETED_INFO'));
        } else {
          setSelectedId(null);
        }
      }

      message.success(t('INVOICE_PROFILE_DELETED'));
    } catch (e: any) {
      console.error('Invoice profile delete error:', e);
      message.error(e?.response?.data?.message || t('INVOICE_PROFILE_DELETE_FAILED'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete-${id}`]: false }));
    }
  };

  const handleEdit = (item: any) => {
    setEditing(item);
    // form.setFieldsValue(item); // Form, InvoiceForm içinde yönetiliyorsa burada gerekmez
    setOpen(true);
  };

  const handleInsert = () => {
    setEditing(null);
    // form.resetFields?.();
    setOpen(true);
  };

  const label = (icon: any, text: string) => (
    <Space size={6}>
      {React.cloneElement(icon, { style: { color: '#1677ff' } })}
      <span style={{ fontWeight: 500 }}>{text}</span>
    </Space>
  );

  // Hata durumu
  if (error && !loading) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>{t('MENU_INVOICE_PROFILES')}</Title>
        </Space>

        <Result
          status="error"
          title={t('INVOICE_PROFILES_LOAD_FAILED_TITLE')}
          subTitle={error}
          extra={[
            <Button
              type="primary"
              key="retry"
              icon={<ReloadOutlined />}
              onClick={() => load()}
              loading={loading}
            >
              {t('BTN_TRY_AGAIN')}
            </Button>,
            <Button
              key="refresh"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              {t('BTN_REFRESH_PAGE')}
            </Button>,
          ]}
        />
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>{t('MENU_INVOICE_PROFILES')}</Title>
          <Text type="secondary">
            {t('INVOICE_PROFILES_DESCRIPTION')}
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load(true)}
            loading={loading}
            disabled={loading}
          >
            {t('BTN_REFRESH')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleInsert}
            disabled={loading}
          >
            {t('INVOICE_ADD_NEW_PROFILE')}
          </Button>
        </Space>
      </Space>

      {/* Bilgi mesajı */}
      {items.length > 0 && !loading && (
        <Alert
          banner
          message={`${items.length} ${t('INVOICE_PROFILES_FOUND')}`}
          description={t('INVOICE_DEFAULT_DESCRIPTION')}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <InvoiceProfileLoadingItems loading={loading} />
      ) : items.length === 0 ? (
        <Card>
          <Empty
            description={
              <div>
                <Text strong>{t('INVOICE_NO_PROFILES')}</Text>
                <br />
                <Text type="secondary">{t('INVOICE_CREATE_FIRST_PROFILE')}</Text>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleInsert}>
              {t('INVOICE_CREATE_FIRST_PROFILE_BTN')}
            </Button>
          </Empty>
        </Card>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {items.map((item: any) => (
            <InvoiceProfileCard actionLoading={actionLoading} handleDelete={handleDelete} handleEdit={handleEdit} handleSelect={handleSelect} key={item.Id} item={item} selectedId={selectedId} loading={loading} />
          ))}
        </Space>
      )}

      {/* Insert/Update Modal (form içeride çok dilli olmalı) */}
      <InvoiceForm
        open={open}
        onClose={() => setOpen(false)}
        editing={editing}
        reload={load}
        loading={!!actionLoading.submit}
      />
    </Space>
  );
}
